import {
  getImageSchedulesIsDirty,
  getImagingScheduleDetails,
} from './ImagingScheduleData';
import { ExperimentInfoImagingScheduleDto } from 'components/Experiment/constants';
import { IExperimentImagingScheduleEntryDTO } from 'business/models/Models';

export function getImagingScheduleOptions(imagingScheduleEntries, selectedSchedule) {
  const selectedEntries = [];
  for (let i = 0; i < imagingScheduleEntries.length; i++) {
    if (imagingScheduleEntries[i].id == selectedSchedule) {
      selectedEntries.push(imagingScheduleEntries[i]);
    }
  }
  const selectedEntriesWithUniqueRows = [];
  for (let i = 0; i < selectedEntries.length; i++) {
    const newEntry = {
      id: selectedEntries[i].id,
      cpName: selectedEntries[i].cpName,
      days: selectedEntries[i].days,
      hours: selectedEntries[i].hours,
      isSelected: selectedEntries[i].isSelected,
      entryId: selectedEntries[i].entryId,
    };
    selectedEntriesWithUniqueRows.push(newEntry);
  }
  return selectedEntriesWithUniqueRows;
}

export function getUniqueScheduleRows(
  entries: IExperimentImagingScheduleEntryDTO[],
): IExperimentImagingScheduleEntryDTO[] {
  const uniqueRows: IExperimentImagingScheduleEntryDTO[] = [];
  entries.forEach((entry) => {
    const exists = uniqueRows.find(
      (row) =>
        row.days === entry.days &&
        row.hours === entry.hours &&
        row.entryId === entry.entryId,
    );
    if (!exists) {
      uniqueRows.push(entry);
    }
  });
  return uniqueRows;
}

export function rowHasImagingSelection(
  entries: IExperimentImagingScheduleEntryDTO[],
  scheduleId: number,
  days: number,
  hours: number,
  entryId: number,
) {
  return entries.some(
    (entry) =>
      entry.id === scheduleId &&
      entry.days === days &&
      entry.hours === hours &&
      entry.entryId === entryId &&
      entry.isSelected &&
      entry.cpName,
  );
}

export function resolveImagingScheduleForValidation(
  expImagingSchedule?: ExperimentInfoImagingScheduleDto,
) {
  if (!expImagingSchedule) {
    return expImagingSchedule;
  }

  if (expImagingSchedule.imagingScheduleEntries?.length) {
    return expImagingSchedule;
  }

  const localDetails = getImagingScheduleDetails();
  if (getImageSchedulesIsDirty() && localDetails?.imagingScheduleEntries?.length) {
    return {
      ...expImagingSchedule,
      selectedImagingScheduleId: localDetails.selectedImagingScheduleId,
      imagingSchedules: localDetails.imagingSchedules?.length
        ? localDetails.imagingSchedules
        : expImagingSchedule.imagingSchedules,
      imagingScheduleEntries: localDetails.imagingScheduleEntries,
      headers: localDetails.headers?.length
        ? localDetails.headers
        : expImagingSchedule.headers,
    };
  }

  return expImagingSchedule;
}

export function isCustomImagingScheduleSelected(
  imagingSchedule?: ExperimentInfoImagingScheduleDto,
) {
  const selectedSchedule = imagingSchedule?.imagingSchedules?.find(
    (schedule) => schedule.id === imagingSchedule?.selectedImagingScheduleId,
  );
  return selectedSchedule?.name === 'Custom';
}

export function hasUnselectedImagingRows(
  imagingSchedule?: ExperimentInfoImagingScheduleDto,
) {
  if (!imagingSchedule?.imagingScheduleEntries?.length) {
    return false;
  }

  const selectedScheduleId = imagingSchedule.selectedImagingScheduleId;
  const selectedSchedule = imagingSchedule.imagingSchedules?.find(
    (schedule) => schedule.id === selectedScheduleId,
  );
  if (!selectedSchedule) {
    return false;
  }

  const scheduleEntries = imagingSchedule.imagingScheduleEntries.filter(
    (entry) => entry.id === selectedScheduleId,
  );
  const uniqueRows = getUniqueScheduleRows(scheduleEntries);

  return uniqueRows.some(
    (row) =>
      !rowHasImagingSelection(
        imagingSchedule.imagingScheduleEntries,
        selectedScheduleId,
        row.days,
        row.hours,
        row.entryId,
      ),
  );
}

export function copySelectedScheduleToCustom(
  imagingScheduleEntries: IExperimentImagingScheduleEntryDTO[],
  customId: number,
  selectedScheduleId: number,
) {
  const newOptions = [];
  imagingScheduleEntries.forEach((option) => {
    if (option.id == selectedScheduleId) {
      const entry = {
        days: option.days,
        hours: option.hours,
        cpName: option.cpName,
        id: customId,
        isSelected: !!(option.cpName && option.isSelected),
        entryId: -1 * option.entryId,
      };
      newOptions.push(option);
      newOptions.push(entry);
    } else if (option.id !== customId) {
      newOptions.push(option);
    }
  });
  return newOptions;
}

export function removeUnselectedRowsFromCustomSchedule(
  imagingSchedule: ExperimentInfoImagingScheduleDto,
) {
  const customId = imagingSchedule.selectedImagingScheduleId;
  const scheduleEntries = imagingSchedule.imagingScheduleEntries.filter(
    (entry) => entry.id === customId,
  );
  const uniqueRows = getUniqueScheduleRows(scheduleEntries);
  const rowsToKeep = uniqueRows.filter((row) =>
    rowHasImagingSelection(
      imagingSchedule.imagingScheduleEntries,
      customId,
      row.days,
      row.hours,
      row.entryId,
    ),
  );
  const rowKeys = new Set(
    rowsToKeep.map((row) => `${row.days}-${row.hours}-${row.entryId}`),
  );

  const imagingScheduleEntries = imagingSchedule.imagingScheduleEntries.filter(
    (entry) => {
      if (entry.id !== customId) {
        return true;
      }
      const rowKey = `${entry.days}-${entry.hours}-${entry.entryId}`;
      if (!rowKeys.has(rowKey)) {
        return false;
      }
      return entry.isSelected && entry.cpName;
    },
  );

  return {
    ...imagingSchedule,
    imagingScheduleEntries,
  };
}

export function createCustomScheduleFromSelected(
  imagingSchedule: ExperimentInfoImagingScheduleDto,
) {
  const selectedScheduleId = imagingSchedule.selectedImagingScheduleId;
  const selectedSchedule = imagingSchedule.imagingSchedules?.find(
    (schedule) => schedule.id === selectedScheduleId,
  );

  if (selectedSchedule?.name === 'Custom') {
    return removeUnselectedRowsFromCustomSchedule(imagingSchedule);
  }

  let customId = imagingSchedule.imagingSchedules?.find(
    (schedule) => schedule.name === 'Custom',
  )?.id;

  const imagingSchedules = [...(imagingSchedule.imagingSchedules || [])];
  if (customId == null) {
    customId = -1;
    imagingSchedules.push({ id: -1, name: 'Custom' });
  }

  const scheduleEntries = imagingSchedule.imagingScheduleEntries.filter(
    (entry) => entry.id === selectedScheduleId,
  );
  const uniqueRows = getUniqueScheduleRows(scheduleEntries);
  const customEntries = [];
  const retainedEntries = imagingSchedule.imagingScheduleEntries.filter(
    (entry) => entry.id !== customId,
  );

  uniqueRows.forEach((row) => {
    if (
      !rowHasImagingSelection(
        imagingSchedule.imagingScheduleEntries,
        selectedScheduleId,
        row.days,
        row.hours,
        row.entryId,
      )
    ) {
      return;
    }

    imagingSchedule.imagingScheduleEntries
      .filter(
        (entry) =>
          entry.id === selectedScheduleId &&
          entry.days === row.days &&
          entry.hours === row.hours &&
          entry.entryId === row.entryId &&
          entry.isSelected &&
          entry.cpName,
      )
      .forEach((option) => {
        customEntries.push({
          days: option.days,
          hours: option.hours,
          cpName: option.cpName,
          id: customId,
          isSelected: true,
          entryId: -1 * option.entryId,
        });
      });
  });

  return {
    ...imagingSchedule,
    selectedImagingScheduleId: customId,
    imagingSchedules,
    imagingScheduleEntries: [...retainedEntries, ...customEntries],
  };
}
